import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CharacterSheet } from '../types/CharacterSheet';
import { ProfileManager } from '../components/ProfileManager';
import { createRoom, leaveRoom, roomExists } from '../utils/roomClient';
import { setLastVisit, getProfileById, setRoomProfileId, updateProfile } from '../utils/profiles';
import { Rules } from '../components/Rules';
import { RoomSelector } from '../components/RoomSelector';
import { encodeRoomId, toRoomId } from '../utils/rooms';
import { RoomWorkspace } from '../components/RoomWorkspace';
import { markGmRoomCreated } from '../utils/gmFlow';
import { RoomTheme } from '../types/RoomTheme';
import { DEFAULT_ROOM_MODE, RoomMode } from '../types/RoomMode';

type AppRoutesProps = {
  formData: CharacterSheet;
  playerId: string;
  roomId: string | null;
  players: Record<string, any>;
  setRoomId: (id: string | null) => void;
  setPlayers: (players: Record<string, any>) => void;
  setActiveProfileIdState: (id: string) => void;
  setFormData: (data: CharacterSheet) => void;
  setShowProfileManager: (show: boolean) => void;
  showProfileManager: boolean;
  activeProfileId: string | null;
  roomTheme: RoomTheme;
  onRoomThemeChange: (theme: RoomTheme) => void;
};

const AppRoutes: React.FC<AppRoutesProps> = ({
  formData,
  playerId,
  roomId,
  players,
  setRoomId,
  setPlayers,
  setActiveProfileIdState,
  setFormData,
  setShowProfileManager,
  showProfileManager,
  activeProfileId,
  roomTheme,
  onRoomThemeChange,
}) => {
  const navigate = useNavigate();

  const handleRoomJoin = async (id: string): Promise<{ ok: boolean; reason?: 'not-found' }> => {
      const roomKey = toRoomId(id);
      if (!roomKey) return { ok: false, reason: 'not-found' };
      const exists = await roomExists(roomKey);
      if (!exists) {
          return { ok: false, reason: 'not-found' };
      }
      setRoomId(roomKey);
      navigate(`/sala/${encodeRoomId(roomKey)}`);
      return { ok: true };
  };

  const handleCreateRoom = async (id: string, theme: RoomTheme, mode: RoomMode = DEFAULT_ROOM_MODE): Promise<{ roomId: string; created: boolean }> => {
      const roomKey = toRoomId(id);
      if (!roomKey) {
          return { roomId: '', created: false };
      }
      try {
          const result = await createRoom(roomKey, playerId, theme, mode);
          if (result.created) {
              markGmRoomCreated(result.roomId, playerId);
          }
          return result;
      } catch (error) {
          console.error('createRoom failed', error);
          return { roomId: roomKey, created: false };
      }
  };
  
    const handleRoomLeave = async () => {
        if (!roomId) return;
        try {
            await leaveRoom(roomId, playerId);
        } catch (err) {
            console.error('leaveRoom failed', err);
        } finally {
            setRoomId(null);
            setPlayers({});
            navigate('/');
        }
    };

    const handleSelectProfile = (id: string) => {
        setActiveProfileIdState(id);
        const profile = getProfileById(id);
        setFormData(profile?.data ?? {});
        if (roomId) {
            setRoomProfileId(roomId, id);
        }
        setShowProfileManager(false);
        setLastVisit(Date.now());
    };

    const handleFormChange = (data: CharacterSheet) => {
        setFormData(data);
        if (activeProfileId) {
            updateProfile(activeProfileId, data);
        }
    };

  return (
    <Routes>
        <Route
            path="/sala/:roomId"
            element={
                roomId ? (
                    <RoomWorkspace
                        roomId={roomId}
                        players={players}
                        playerId={playerId}
                        formData={formData}
                        activeProfileId={activeProfileId}
                        showProfileManager={showProfileManager}
                        onFormChange={handleFormChange}
                        onSelectProfile={handleSelectProfile}
                        onToggleProfileManager={setShowProfileManager}
                        onLeaveRoom={handleRoomLeave}
                        roomTheme={roomTheme}
                        onRoomThemeChange={onRoomThemeChange}
                    />
                ) : (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Cargando sala...</div>
                )
            }
        />

        <Route path="/personaje" element={<Navigate to="/" replace />} />

        <Route path="/sala" element={<Navigate to="/" replace />} />

        <Route
            path="/reglas"
            element={
                <div>
                    <Rules />
                </div>
            }
        />

        <Route
            path="/gestion"
            element={
                <div>
                    <ProfileManager onSelect={handleSelectProfile} navigateOnSelect={null} />
                </div>
            }
        />

        <Route
            path="/"
            element={<RoomSelector onJoinRoom={handleRoomJoin} onCreateRoom={handleCreateRoom} />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};


export default AppRoutes;
